import React, { useEffect, useMemo, useState } from "react";
import { clone, isEmpty, isNil } from "ramda";
import { useIntl } from "react-intl";
import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { getAlertThemeData, getMuiTheme } from "../../../utils/utils";
import palette from "../../../../config/colorPalette";
import { ErrorText } from "../styled";
import { postAlertConfiguration } from "../../../api";
import { setConfigDataAction } from "../../../redux/configSlice";

const AlertConfigEquipment = ({
  open,
  setOpen,
  configData,
  selectedAsset,
  selectedEquipment,
  equipmentTypes,
  disabledRole,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useMemo(() => getMuiTheme(), []);
  const listCategory = ["equipment"];
  const [data, setData] = useState(null);
  const [alertName, setAlertName] = useState("");
  const alertCategory = "equipment";
  const [alertType, setAlertType] = useState("stats");
  const [alertSubType, setAlertSubType] = useState("data_disk_usage");
  const conditionOptions =
    alertSubType === "common"
      ? ["between", "outside_range", "in_set", "not_in_set", "exist"]
      : [">", ">=", "==", "<=", "<"];

  useEffect(() => {
    let finalData = {};
    for (let indexCategory = 0; indexCategory < listCategory.length; indexCategory++) {
      if (listCategory[indexCategory] === "equipment") {
        finalData[listCategory[indexCategory]] = {
          stats: ["data_disk_usage", "cpu_usage"],
          error_codes: ["common"],
        };
      }
    }
    setData(finalData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset]);

  const [errors, setErrors] = useState([]);
  useEffect(() => {
    if (alertName.length < 3) {
      setErrors((prevErrors) => ({ ...prevErrors, "alert-name": "3.characters" }));
    } else {
      setErrors((prevErrors) => {
        const { "alert-name": alertName, ...restErrors } = prevErrors;
        return restErrors;
      });
    }
  }, [alertName, setErrors]);

  const [alertActive, setAlertActive] = useState(true);
  const [alertSeverity, setAlertSeverity] = useState(1);
  const [alertThresholdTypes, setAlertThresholdTypes] = useState([]);
  const [alertTresholdConditions, setAlertTresholdConditions] = useState([]);
  const [alertThresholdValue, setAlertThresholdValue] = useState([]);
  const [alertThresholdValues, setAlertThresholdValues] = useState([]);
  const [alertThresholdFields, setAlertThresholdFields] = useState([]);
  const [alertThresholdDuration, setAlertThresholdDuration] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const handleClose = (setOpen) => {
    setOpen(false);
  };
  const disabled = useMemo(() => !isEmpty(errors), [errors]);

  return (
    <ThemeProvider theme={theme}>
      <Dialog maxWidth="lg" open={open} onClose={() => handleClose(setOpen)}>
        <DialogTitle style={{ fontSize: "x-large", color: palette.primary, fontWeight: "600" }}>
          {`${intl.formatMessage({ id: "alert.config" })}`}
        </DialogTitle>
        <DialogContent style={{ paddingBottom: "0" }}>
          <div>
            <Divider style={{ marginBottom: "1rem", color: palette.primary }} />
            <div style={{ display: "flex" }}>
              <FormControl>
                <TextField
                  autoFocus
                  value={alertName}
                  variant="outlined"
                  autoComplete="off"
                  margin="dense"
                  id="alert-name"
                  label={intl.formatMessage({ id: "name" })}
                  type="text"
                  fullWidth
                  required
                  disabled={disabledRole}
                  error={errors["alert-name"] ? true : false}
                  onChange={(e) => {
                    setAlertName(e.target.value);
                  }}
                ></TextField>
                {!isNil(errors["alert-name"]) && (
                  <ErrorText>{intl.formatMessage({ id: errors["alert-name"] })}</ErrorText>
                )}
                <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="flex-start">
                  <Grid item>
                    <TextField
                      id="alertType"
                      margin="dense"
                      sx={{ width: "10rem" }}
                      variant="outlined"
                      value={alertType}
                      disabled={disabledRole}
                      onChange={(e) => {
                        setAlertType(e.target.value);
                        if (!Array.isArray(data[alertCategory])) {
                          setAlertSubType(data[alertCategory][e.target.value][0]);
                          let newConditions = clone(alertTresholdConditions);
                          if (data[alertCategory][e.target.value][0] === "common") {
                            for (let conIndex = 0; conIndex < alertTresholdConditions.length; conIndex++) {
                              newConditions[conIndex] = "between";
                            }
                          } else {
                            for (let conIndex = 0; conIndex < alertTresholdConditions.length; conIndex++) {
                              newConditions[conIndex] = ">";
                            }
                          }
                          setAlertTresholdConditions(newConditions);
                        }
                      }}
                      select
                      label={intl.formatMessage({ id: "alert.type" })}
                      required
                    >
                      {!isNil(data) && Array.isArray(data[alertCategory])
                        ? data[alertCategory].map((type) => (
                            <MenuItem key={`ref_${type}`} value={type}>
                              {intl.formatMessage({ id: type })}
                            </MenuItem>
                          ))
                        : !isNil(data) &&
                          Object.keys(data[alertCategory]).map((type) => (
                            <MenuItem key={`ref_${type}`} value={type}>
                              {intl.formatMessage({ id: type })}
                            </MenuItem>
                          ))}
                    </TextField>
                  </Grid>
                  {!isNil(data) && !Array.isArray(data[alertCategory]) && (
                    <Grid item>
                      <TextField
                        id="alertType"
                        margin="dense"
                        sx={{ width: "10rem" }}
                        variant="outlined"
                        value={alertSubType}
                        disabled={disabledRole}
                        onChange={(e) => {
                          setAlertSubType(e.target.value);
                        }}
                        select
                        label={intl.formatMessage({ id: "alert.subtype" })}
                        required
                      >
                        {!isNil(data) &&
                          !Array.isArray(data[alertCategory]) &&
                          data[alertCategory][alertType].map((subtype) => (
                            <MenuItem key={`sub_${subtype}`} value={subtype}>
                              {intl.formatMessage({ id: subtype })}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Grid>
                  )}
                </Grid>
                <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="flex-start">
                  <Grid item>
                    <FormControlLabel
                      control={
                        <Checkbox
                          id={"checkbox_active"}
                          sx={{
                            color: palette.primary,
                            "&.Mui-checked": {
                              color: palette.primary,
                            },
                            marginLeft: "15%",
                            marginTop: "0.5rem",
                          }}
                          defaultChecked={alertActive}
                          disabled={disabledRole}
                          onChange={(e) => {
                            setAlertActive(e.target.checked);
                          }}
                        />
                      }
                      label={intl.formatMessage({ id: "active" })}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      id="alertSeverity"
                      margin="dense"
                      sx={{ width: "10rem" }}
                      variant="outlined"
                      value={alertSeverity}
                      onChange={(e) => setAlertSeverity(e.target.value)}
                      disabled={disabledRole}
                      select
                      label={intl.formatMessage({ id: "alert.severity" })}
                      required
                    >
                      <MenuItem key={`severity_info`} value={1}>
                        {(() => {
                          const alertData = getAlertThemeData(1, "small", intl);

                          return (
                            <Badge
                              sx={{
                                backgroundColor: alertData.backgroundColor,
                                justifyContent: "center",
                                borderRadius: "16px",
                                padding: "5px",
                                marginRight: "0.5rem",
                              }}
                            >
                              {alertData.icon}
                            </Badge>
                          );
                        })()}
                        {intl.formatMessage({ id: "info" })}
                      </MenuItem>
                      <MenuItem key={`severity_warning`} value={2}>
                        {(() => {
                          const alertData = getAlertThemeData(2, "small", intl);

                          return (
                            <Badge
                              sx={{
                                backgroundColor: alertData.backgroundColor,
                                justifyContent: "center",
                                borderRadius: "16px",
                                padding: "5px",
                                marginRight: "0.5rem",
                              }}
                            >
                              {alertData.icon}
                            </Badge>
                          );
                        })()}{" "}
                        {intl.formatMessage({ id: "warning" })}
                      </MenuItem>
                      <MenuItem key={`severity_critical`} value={3}>
                        {(() => {
                          const alertData = getAlertThemeData(3, "small", intl);

                          return (
                            <Badge
                              sx={{
                                backgroundColor: alertData.backgroundColor,
                                justifyContent: "center",
                                borderRadius: "16px",
                                padding: "5px",
                                marginRight: "0.5rem",
                              }}
                            >
                              {alertData.icon}
                            </Badge>
                          );
                        })()}
                        {intl.formatMessage({ id: "critical" })}
                      </MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                <h2>{intl.formatMessage({ id: "thresholds" })}</h2>
                <Button
                  variant="contained"
                  style={{ backgroundColor: palette.primary, color: "white", width: "6rem", marginTop: "1%" }}
                  onClick={() => {
                    setAlertThresholdTypes([...alertThresholdTypes, "normal"]);
                    setAlertTresholdConditions([...alertTresholdConditions, ">"]);
                    setAlertThresholdValue([...alertThresholdValue, 0]);
                    setAlertThresholdValues([...alertThresholdValues, []]);
                    setAlertThresholdFields([...alertThresholdFields, "substructure"]);
                    setAlertThresholdDuration([...alertThresholdDuration, 0]);
                  }}
                >
                  {intl.formatMessage({ id: "add.threshold" })}
                </Button>
                {!isEmpty(alertThresholdTypes) &&
                  alertThresholdTypes.map((at, atIndex) => {
                    return (
                      <Grid
                        spacing={1}
                        container
                        direction="row"
                        justifyContent="center"
                        sx={{ marginTop: "1%" }}
                        alignItems="flex-start"
                        key={`grid${atIndex}`}
                      >
                        <Grid item>
                          <TextField
                            id={`alertThresholdCondition${atIndex}`}
                            margin="dense"
                            sx={{ width: "10rem" }}
                            variant="outlined"
                            value={alertTresholdConditions[atIndex]}
                            disabled={disabledRole}
                            onChange={(e) => {
                              let newConditions = clone(alertTresholdConditions);
                              newConditions[atIndex] = e.target.value;
                              setAlertTresholdConditions(newConditions);
                            }}
                            select
                            label={intl.formatMessage({ id: "threshold.condition" })}
                            required
                          >
                            {conditionOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {intl.formatMessage({ id: option })}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        {alertSubType !== "common" && (
                          <Grid item>
                            <TextField
                              value={alertThresholdValue[atIndex]}
                              variant="outlined"
                              autoComplete="off"
                              margin="dense"
                              id={`alertThresholdValue_${atIndex}`}
                              label={`${intl.formatMessage({ id: "value" })} %`}
                              type="number"
                              fullWidth
                              required
                              disabled={disabledRole}
                              error={errors[`alertThresholdValue_${atIndex}`] ? true : false}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);
                                let newValues = clone(alertThresholdValue);
                                newValues[atIndex] = e.target.value;
                                setAlertThresholdValue(newValues);
                              }}
                            ></TextField>
                          </Grid>
                        )}

                        {alertSubType === "cpu_usage" && (
                          <Grid item>
                            <TextField
                              //value={alertThresholdValue[atIndex]}
                              variant="outlined"
                              autoComplete="off"
                              margin="dense"
                              id={`alertDuration_${atIndex}`}
                              label={intl.formatMessage({ id: "duration" })}
                              type="number"
                              fullWidth
                              required
                              disabled={disabledRole}
                              error={errors[`alertDuration_${atIndex}`] ? true : false}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);
                                let newValues = clone(alertThresholdDuration);
                                newValues[atIndex] = e.target.value;
                                setAlertThresholdDuration(newValues);
                              }}
                            ></TextField>
                          </Grid>
                        )}
                        {alertSubType === "common" && (
                          <Grid item>
                            <Autocomplete
                              multiple
                              freeSolo
                              sx={{ width: "10rem", marginTop: "5%" }}
                              options={[]}
                              disabled={disabledRole}
                              filterOptions={(options) => options}
                              onChange={(event, newValue) => {
                                const onlyNumbers = newValue.filter((value) => !isNaN(value));
                                let newValues = clone(alertThresholdValues);
                                newValues[atIndex] = onlyNumbers;
                                setAlertThresholdValues(newValues);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={`${intl.formatMessage({ id: "enter.numbers" })}`}
                                  variant="outlined"
                                  onKeyDown={(e) => {
                                    if (!/[0-9]/.test(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        )}
                        {alertSubType === "fiber_break" && (
                          <Grid item>
                            <TextField
                              id={`alertField${atIndex}`}
                              margin="dense"
                              sx={{ width: "10rem" }}
                              variant="outlined"
                              disabled={disabledRole}
                              value={alertThresholdFields[atIndex]}
                              onChange={(e) => {
                                let newConditions = clone(alertThresholdFields);
                                newConditions[atIndex] = e.target.value;
                                setAlertThresholdFields(newConditions);
                              }}
                              select
                              label={intl.formatMessage({ id: "field" })}
                              required
                            >
                              <MenuItem key={`field_substructure`} value={"substructure"}>
                                {`substructure`}
                              </MenuItem>
                            </TextField>
                          </Grid>
                        )}
                      </Grid>
                    );
                  })}
              </FormControl>
            </div>
            <Divider style={{ marginTop: "1rem" }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            style={{ backgroundColor: palette.secondary, color: "white" }}
            onClick={() => handleClose(setOpen)}
          >
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          {
            <Button
              type="button"
              variant="contained"
              style={{
                backgroundColor: disabled ? palette.light : palette.primary,
              }}
              disabled={disabled}
              id={"save_data_button"}
              onClick={async (e) => {
                const thresholds = [];
                for (let index = 0; index < alertThresholdTypes.length; index++) {
                  const newTH = {
                    type: alertThresholdTypes[index],
                    condition: alertTresholdConditions[index],
                    value: alertThresholdValue[index],
                    values: [],
                    duration: 8,
                    field: null,
                  };
                  thresholds.push(newTH);
                }
                const finalData = {
                  name: alertName,
                  desc: null,
                  type: alertType,
                  category: alertCategory,
                  subtype: alertSubType,
                  active: alertActive,
                  severity: alertSeverity,
                  conditions: thresholds,
                };
                const response = await postAlertConfiguration(finalData, intl);
                if (response?.status === 200) {
                  dispatch(
                    setConfigDataAction({
                      ...configData,
                      alertConfiguration: [...configData.alertConfiguration, finalData],
                    }),
                  );
                  handleClose(setOpen);
                }
              }}
            >
              {intl.formatMessage({ id: "accept" })}
            </Button>
          }
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AlertConfigEquipment;
